import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AlertTitle, AlertView, Poppins } from 'src/common';
import { CsvVariable } from './VariablesCsvUpload';
import _ from 'lodash';
import CurrencyInput from 'src/components/form/CurrencyInput';
import Input from 'src/components/form/Input';
import MaterialInput from 'src/components/form/MaterialInput';
import { validateVariableName } from 'src/components/formComposed/WorkspaceVariablesForm/util';

const VariableRow = styled.div`
  display: grid;
  grid-template-columns: 16px 1fr 0.5fr 1.4fr 142px 1fr;
  gap: 12px;
  align-items: center;
  /* border: 1px solid red; */
  max-width: 1100px;

  .grid-item {
    /* outline: 1px solid blue; */

    &__divider {
      background-color: ${({ theme }) => theme.colors.white};
      height: 1px;
      z-index: 1;
      position: relative;
    }

    &__currency-input {
      display: flex;
      grid-template-columns: repeat(auto-fit, minmax(10px, 1fr));
      grid-gap: 10px;
      align-items: center;
      justify-content: flex-end;
    }
  }
`;

const NameInput = styled.input`
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.cflowerBlue};
  cursor: not-allowed;

  &:disabled {
    background: ${({ theme }) => theme.colors.cultured};
  }
`;

const ValueInput = styled.input`
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.prussianBlue};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.brightBlue};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.cflowerBlue};
  }
`;

export interface ManualVariable {
  name: string;
  snakeName: string;
  value: number | null;
  currency: boolean;
}

interface MissingVariablesFormProps {
  missingVariableNames: string[];
  csvVariables: CsvVariable[];
  manualVariables: ManualVariable[];
  onManualVariablesChange: (variables: ManualVariable[]) => void;
}

export const MissingVariablesForm: React.FC<MissingVariablesFormProps> = ({
  missingVariableNames,
  csvVariables,
  manualVariables,
  onManualVariablesChange,
}) => {
  const [touchedNames, setTouchedNames] = useState<Set<string>>(new Set());
  const [touchedValues, setTouchedValues] = useState<Set<string>>(new Set());

  const stillMissingNames = missingVariableNames.filter((name) => {
    const snakeName = _.snakeCase(name);
    return !csvVariables.some((csv) => csv.snakeName === snakeName);
  });

  useEffect(() => {
    const existingNames = new Set(manualVariables.map((v) => v.snakeName));
    const newVariables: ManualVariable[] = [];

    stillMissingNames.forEach((name) => {
      const snakeName = _.snakeCase(name);
      if (!existingNames.has(snakeName)) {
        newVariables.push({
          name: snakeName,
          snakeName,
          value: null,
          currency: false,
        });
      }
    });

    if (newVariables.length > 0) {
      onManualVariablesChange([...manualVariables, ...newVariables]);
    }
  }, [stillMissingNames.join(',')]);

  if (stillMissingNames.length === 0) return null;

  const handleValueChange = (snakeName: string, value: string) => {
    const numValue = value === '' ? null : Number(value);
    onManualVariablesChange(
      manualVariables.map((v) =>
        v.snakeName === snakeName ? { ...v, value: isNaN(numValue as number) ? null : numValue } : v,
      ),
    );
  };

  const handleCurrencyChange = (snakeName: string, currency: boolean) => {
    onManualVariablesChange(manualVariables.map((v) => (v.snakeName === snakeName ? { ...v, currency } : v)));
  };

  const handleNameChange = (snakeName: string, name: string) => {
    onManualVariablesChange(manualVariables.map((v) => (v.snakeName === snakeName ? { ...v, name } : v)));
  };

  const handleNameBlur = (snakeName: string, name: string) => {
    setTouchedNames((prev) => new Set(prev).add(snakeName));
    const trimmed = name.trim();
    if (trimmed !== name) {
      onManualVariablesChange(manualVariables.map((v) => (v.snakeName === snakeName ? { ...v, name: trimmed } : v)));
    }
  };

  const handleValueBlur = (snakeName: string) => {
    setTouchedValues((prev) => new Set(prev).add(snakeName));
    const variable = manualVariables.find((v) => v.snakeName === snakeName);
    if (variable) {
      handleValueChange(snakeName, variable.value?.toString() ?? '');
    }
  };

  const getNameError = (variable: ManualVariable): string | null => {
    return validateVariableName(variable.name);
  };

  const getValueError = (variable: ManualVariable): boolean => {
    return variable.value === null || variable.value < 0;
  };

  const displayVariables = manualVariables.filter((v) =>
    stillMissingNames.some((name) => _.snakeCase(name) === v.snakeName),
  );

  if (displayVariables.length === 0) return null;

  return (
    <AlertView>
      <AlertTitle>
        <Poppins px={14} color="statusRed" weight={600} style={{ fontStyle: 'italic' }}>
          The following variables are unrecognised. Please add names and values to these variables.
        </Poppins>
      </AlertTitle>

      {displayVariables.map((el) => {
        const nameError = getNameError(el);
        const valueError = getValueError(el);
        const isNameTouched = touchedNames.has(el.snakeName);
        const isValueTouched = touchedValues.has(el.snakeName);
        return (
          <VariableRow key={el.snakeName}>
            <div className="grid-item"></div>
            <div className="grid-item">
              <Poppins px={14} weight={600} color="statusRed" style={{ fontStyle: 'italic' }}>
                #{el.snakeName}
              </Poppins>
            </div>
            <div className="grid-item">
              <div className="grid-item__divider" />
            </div>
            <div className="grid-item">
              <MaterialInput
                placeholder="Add variable name *"
                value={el.name}
                onChange={(e) => handleNameChange(el.snakeName, e.target.value)}
                onBlur={(e) => handleNameBlur(el.snakeName, e.target.value)}
                fullWidth={true}
                error={isNameTouched && !!nameError}
              />
            </div>

            <div className="grid-item">
              <div className="grid-item__currency-input">
                <Poppins px={14} weight={500} color="cflowerBlue" nowrap className="currency-label">
                  CURRENCY
                </Poppins>
                <Input
                  checked={el.currency}
                  onChange={(e) => {
                    handleCurrencyChange(el.snakeName, e.target.checked);
                  }}
                  className="switch"
                  type="checkbox"
                />
              </div>
            </div>

            <div className="grid-item">
              <CurrencyInput
                placeholder={`Enter value *`}
                value={el.value}
                currency={el.currency}
                onValueChange={(e) => handleValueChange(el.snakeName, e.floatValue.toString())}
                onBlur={() => handleValueBlur(el.snakeName)}
                fullWidth={true}
                error={isValueTouched && valueError}
              />
            </div>
          </VariableRow>
        );
      })}
    </AlertView>
  );
};
